
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Card,
  CardContent,
  Box,
  Button,
  Typography,
  TableContainer,
  InputAdornment,
  TextField,
  CircularProgress,
  Badge,
  Tooltip,
  TablePagination,
  Alert,
  Chip,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  FiberManualRecord as DotIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { request } from '../../../../../utils/request';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from '../../../../../utils/useDebounce';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';

import DeleteConfirmDialog from '../../../../../componentsUiBlock/DeleteConfirmDialog';
import { useDeleteDialog } from '../../../../../utils/useDeleteDialog';
import { Party } from '../../../types';
import { usePermissions } from '../../../../../utils/usePermissions';

interface HeadCell {
  id: keyof Party | 'action' | 'contactInfo' | 'locationInfo' | 'entityCategory';
  label: string;
  sortable?: boolean;
}

const categoryType = [ 'PARTY', 'WALK_IN_CUSTOMER', 'REGULAR_CUSTOMER', 'WEBSITE']

const headCells: HeadCell[] = [
  { id: 'partyName', label: 'Party', sortable: true },
  { id: 'contactInfo', label: 'Contact', sortable: false },
  { id: 'locationInfo', label: 'Location', sortable: true },
  { id: 'entityCategory', label: 'Customer Type', sortable: true },
  { id: 'action', label: 'Action', sortable: false }
];

// Customer type filter options
const entityCategoryFilterOptions = [
  { value: 'ALL', label: 'All Types' },
  { value: 'PARTY', label: 'Party' },
  { value: 'WALK_IN_CUSTOMER', label: 'Walk In Customer' },
  { value: 'REGULAR_CUSTOMER', label: 'Regular Customer' },
  { value: 'WEBSITE', label: 'Website' },
];

const PartiesList: React.FC = () => {
  const { canCreateParties, canDeleteParties } = usePermissions();
  const [parties, setParties] = useState<Party[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof Party>('partyName');
  const [search, setSearch] = useState('');
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Filter state
  const [entityCategoryFilter, setEntityCategoryFilter] = useState<string>('ALL');
  const [, setSelectedEntityCategories] = useState<string[]>(categoryType);

  const navigate = useNavigate();
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    const getPartyData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: (page + 1).toString(),
          limit: rowsPerPage.toString(),
        });

        if (debouncedSearch) {
          params.append('q', debouncedSearch);
        }

        // Add customer type filter to API call if not "ALL"
        if (entityCategoryFilter !== 'ALL') {
          params.append('entityCategory', entityCategoryFilter);
        }

        const url = `party/get-all-parties?${params.toString()}`;

        const res = await request({
          url: url,
          method: 'get',
        });
        if (res.parties) {
          setParties(res.parties);
          setTotal(res.total);
        }
      } catch (error: any) {
        console.error(error);
        toast.error(error || 'Failed to fetch parties');
      } finally {
        setLoading(false);
      }
    };
    getPartyData();
  }, [debouncedSearch, page, rowsPerPage, entityCategoryFilter]);

  const handleSort = (property: HeadCell['id']) => {
    if (!headCells.find(cell => cell.id === property)?.sortable) return;

    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property as keyof Party);
  };

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Handle customer type filter change
  const handleEntityCategoryFilterChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setEntityCategoryFilter(value);

    // Reset to first page when filter changes
    setPage(0);

    // Update selected customer types based on filter
    if (value === 'ALL') {
      setSelectedEntityCategories(categoryType);
    } else {
      setSelectedEntityCategories([value]);
    }
  };

  const sortedParties = [...parties].sort((a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return order === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });

  const deleteParty = async (party: Party) => {
    try {
      const res = await request({
        url: `party/delete-party/${party._id}`,
        method: "delete",
      });
      if (res?.status === "success") {
        toast.success(res?.message);
        // Remove the party from the list
        setParties(prev => prev.filter(pt => pt._id !== party._id));

        // If we deleted the last item on the page, go to previous page
        if (parties.length === 1 && page > 0) {
          setPage(page - 1);
        }
      } else {
        toast.error(res?.message);
      }
    } catch (error: any) {
      toast.error(error);
    }

  }

  const { open, loadingDelete, itemToDelete, openDialog, closeDialog, confirmDelete } = useDeleteDialog<Party>({
    onDelete: deleteParty,
  })

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
        <Brandcrumb />
      </Box>
      <Card elevation={4}>
        <CardContent sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            {canCreateParties() && (
              <Button
                variant="contained"
                size='small'
                color="primary"
                onClick={() => navigate("/parties/addParty")}
                sx={{ flexShrink: 0 }}
                startIcon={<GroupsIcon />}
              >
                Add New Party
              </Button>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
              {/* Customer Type Filter - ADDED SECTION */}
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Entity Category Types" placement='top-start' arrow>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                      value={entityCategoryFilter}
                      onChange={handleEntityCategoryFilterChange}
                      displayEmpty
                      sx={{
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': {
                          py: 0.8,
                        }
                      }}
                    >
                      {entityCategoryFilterOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>

              </Box>

              <Badge badgeContent={total} showZero color="primary">
                <GroupsIcon color="action" />
              </Badge>

              <TextField
                size="small"
                placeholder="Search parties..."
                variant="outlined"
                sx={{ width: { xs: '100%', sm: 300 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
          </Box>
        </CardContent>

        <CardContent sx={{ pt: 1 }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {headCells.map((head) => (
                    <TableCell key={head.id} sx={{ fontWeight: 'bold' }}>
                      {head.sortable !== false ? (
                        <TableSortLabel
                          active={orderBy === head.id}
                          direction={orderBy === head.id ? order : 'asc'}
                          onClick={() => handleSort(head.id)}
                        >
                          {head.label}
                        </TableSortLabel>
                      ) : (
                        head.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {debouncedSearch ? 'Searching...' : 'Loading parties...'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : sortedParties.length > 0 ? (
                  sortedParties.map((party) => (
                    <TableRow key={party._id} hover>
                      {/* Party Name with Status Dot and Nickname */}
                      <TableCell>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                          <Tooltip
                            title={party.status === 'active' ? 'Active Party' : 'Inactive Party'}
                            arrow
                            placement="top-start"
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <DotIcon
                                sx={{
                                  color: party.status === 'active' ? 'success.main' : 'error.main',
                                  fontSize: 16,
                                }}
                              />
                            </Box>
                          </Tooltip>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {party.partyName}
                            </Typography>
                            {party.nickName && (
                              <Typography variant="caption" color="text.secondary">
                                ({party.nickName})
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Contact Info - Single Phone and Email */}
                      <TableCell>
                        <Box sx={{ maxWidth: 150 }}>
                          {/* Primary Phone */}
                          {party.contact.phone.length > 0 && (
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" noWrap title={party.contact.phone[0].phoneNo}>
                                {party.contact.phone[0].phoneNo}
                              </Typography>
                            </Box>
                          )}

                          {/* Primary Email */}
                          {party.contact.email.length > 0 && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" noWrap title={party.contact.email[0]}>
                                {party.contact.email[0]}
                              </Typography>
                            </Box>
                          )}

                          {/* Show counts if more available */}
                          {(party.contact.phone.length > 1 || party.contact.email.length > 1) && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              +{party.contact.phone.length - 1} phone{party.contact.phone.length > 2 ? 's' : ''},
                              +{party.contact.email.length - 1} email{party.contact.email.length > 2 ? 's' : ''}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      {/* Location with City */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                          <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="body2" noWrap>
                            {party?.location}
                          </Typography>
                        </Box>
                        {party?.address?.city && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {party?.address?.city}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Customer Type */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                          <Chip
                            key={party.entityCategory}
                            label={party.entityCategory}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              height: 20,
                              mb: 0.5,
                              '& .MuiChip-label': {
                                px: 0.8,
                              }
                            }}
                          />
                        </Box>
                      </TableCell>

                      {/* Action Buttons */}
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.8}>
                          <Button
                            variant="contained"
                            color='secondary'
                            onClick={() => navigate(`/parties/dailyLedger/${party._id}`)}
                            sx={{
                              minWidth: 'auto',
                              fontSize: '0.60rem',
                              textWrap: 'nowrap',
                              py: 0.1,
                              px: 2,
                              borderRadius: 5,
                              border: '.8px solid #7f5ee666',
                              '&:hover': { backgroundColor: '#7f5ee666' }
                            }}
                          >
                            Daily Ledger
                          </Button>
                          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={0.8}>
                            <Button
                              variant="text"
                              fullWidth
                              onClick={() => navigate(`/parties/${party._id}`)}
                              sx={{
                                minWidth: 'auto',
                                fontSize: '0.60rem',
                                textWrap: 'nowrap',
                                py: 0.1,
                                px: 2,
                                borderRadius: 5,
                                border: '.8px solid #7f5ee666',
                                '&:hover': { backgroundColor: '#7f5ee666' }
                              }}
                            >
                              View / Edit
                            </Button>
                            {canDeleteParties() && (
                              <Button
                                size="small"
                                fullWidth
                                variant="text"
                                color="error"
                                onClick={() => openDialog(party)}
                                sx={{
                                  minWidth: 'auto',
                                  fontSize: '0.60rem',
                                  py: 0.1,
                                  px: 2,
                                  borderRadius: 5,
                                  border: '.8px solid #ff4d6e5e',
                                  '&:hover': { backgroundColor: '#ff4d6e5e' }
                                }}
                              >
                                Delete
                              </Button>
                            )}

                          </Box>

                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        {search ? `No parties found for "${search}"` : 'No parties available. Add your first party!'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Component */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              '.MuiTablePagination-toolbar': {
                padding: 1,
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={open}
        loading={loadingDelete}
        title='Delete Party'
        description={
          <>
            Are you sure you want to delete the party <strong>"{itemToDelete?.partyName}"</strong>? <br /><br />
            <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>This action cannot be undone and all associated data will be permanently removed.</Alert>
          </>
        }
        onCancel={closeDialog}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default PartiesList;